import { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle2, Clock, XCircle, Copy, Home } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

export default function DonateSuccess() {
  const [sp] = useSearchParams();
  const sessionId = sp.get("session_id") || "";
    const [state, setState] = useState({ status: "polling", payment_status: "pending", donor_serial: "" });
  const [tries, setTries] = useState(0);
  const nav = useNavigate();
  useEffect(() => { if (state.payment_status === "paid" && state.donor_serial && state.thank_you_token) nav(`/thank-you/${state.donor_serial}?t=${state.thank_you_token}`, { replace: true }); }, [state, nav]);

  useEffect(() => { document.title = "Donation received | Dutsen Kauna Foundation"; }, []);

  useEffect(() => {
    if (!sessionId) return;
    let alive = true;
    const poll = async () => {
      try {
        const url = `/payments/status/${sessionId}`;
        const { data } = await api.get(url);
        if (!alive) return;
        setState(data);
        if (data.payment_status !== "paid" && !["failed", "abandoned"].includes(data.status) && tries < 20) {
          setTimeout(() => setTries((t) => t + 1), 2500);
        }
      } catch (e) {
        if (!alive) return;
        setState((s) => ({ ...s, status: "error" }));
      }
    };
    poll();
    return () => { alive = false; };
  }, [sessionId, tries]);

  const paid = state.payment_status === "paid";
  const failed = ["failed", "abandoned"].includes(state.status) || state.payment_status === "failed";

  return (
    <div data-testid="donate-success-page" className="bg-[#f8f4e8] min-h-[70vh] py-16">
      <div className="dk-container max-w-2xl">
        <div className="bg-white rounded-2xl p-10 text-center border border-[#d6aa43]/20">
          {paid ? (
            <>
              <CheckCircle2 className="text-[#d6aa43] mx-auto mb-4" size={56} />
              <h1 className="font-serif text-3xl md:text-4xl text-[#0a0d12] font-bold mb-3">Thank you.</h1>
              <p className="text-[#0a0d12]/75 mb-6">Your payment is confirmed. A receipt has been emailed to you with your donor reference.</p>
              {state.donor_serial && (
                <div className="p-6 rounded-xl bg-[#155e3f] text-white mb-6">
                  <div className="text-xs uppercase tracking-[0.25em] text-[#d6aa43] mb-2">Donor reference</div>
                  <div className="font-mono text-2xl md:text-3xl font-bold text-[#d6aa43]" data-testid="paid-donor-serial">{state.donor_serial}</div>
                  <button onClick={() => navigator.clipboard.writeText(state.donor_serial).then(() => toast.success("Copied"))} className="text-xs text-white inline-flex items-center gap-1 mt-2"><Copy size={12} /> Copy</button>
                </div>
              )}
            </>
          ) : failed ? (
            <>
              <XCircle className="text-red-500 mx-auto mb-4" size={56} />
              <h1 className="font-serif text-3xl text-[#0a0d12] font-bold mb-3">Payment did not complete</h1>
              <p className="text-[#0a0d12]/75 mb-6">No charge was made. You can try again anytime.</p>
            </>
          ) : (
            <>
              <Clock className="text-[#d6aa43] mx-auto mb-4 animate-pulse" size={56} />
              <h1 className="font-serif text-3xl text-[#0a0d12] font-bold mb-3">Confirming your payment…</h1>
              <p className="text-[#0a0d12]/75 mb-6">This usually takes a few seconds. Please don't close this tab.</p>
            </>
          )}
          <div className="flex flex-wrap gap-3 justify-center">
            <Link to="/donate" className="dk-btn-outline !text-[#0d1724] !border-[#0d1724] hover:!bg-[#155e3f] hover:!text-[#d6aa43]">Make another donation</Link>
            <Link to="/" className="dk-btn-primary"><Home size={14} /> Return home</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
